import { CloudUploadOutlined } from '@mui/icons-material';
import { TextButton } from 'Components/Button';
import { store } from 'Store';
import { ICONS } from 'Utils/icons';
import { useEffect, useState } from 'react';
import { Row, Col, Card, Badge, Form, Stack, Image } from "react-bootstrap";
import { getProfileImage, saveProfileImage } from './EditUserProfilePictureAxiosFuncs';
import { useDispatch } from 'react-redux';
import { setAvatar } from 'Store/LoginInfoSlice';
import { PageInfo } from "Components/PageInformation/PageInformation";
import { STATUSES, ERROR_MESSAGES, WARNING_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus, setGlobalWarningStatus } from 'Components/StatusHandling';
import EditUserProfilePictureWarningPopup from './EditUserProfilePictureWarningPopup';

const EditUserProfilePicture = () => {

  const updateAvatarChannel = new BroadcastChannel("User_Avatar");
  const dispatch = useDispatch();
  const loginState = store.getState().loginInfo;
  const token = "Bearer " + loginState.token;
  const FILE_FORMAT = ".jpg, .png";
  const FILE_SIZE_LIMIT_ERROR = "File size exceeds 5 MB. Please choose a smaller image.";
  const FILE_TYPE_ERROR = "Unsupported file type. Please upload a .jpg or .png image.";
  const UPLOAD_GUIDE_INFO =
    <>
      Select "<span className="upload-info-highlight">Upload</span>" to update your profile picture.
    </>;
  const UPLOAD_SUCCESS = "Profile picture uploaded successfully.";

  const [fileUrl, setFileUrl] = useState(null);
  const [avatarData, setAvatarData] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [globalErrorList, setGlobalErrorList] = useState([]);
  const [globalInfo, setGlobalInfo] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [showWarningPopup, setShowWarningPopup] = useState(false);

  const getInitials = (firstName, lastName) => {
    if (firstName && lastName) {
      return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();
    }

    return "UNK";
  }

  //validate size and type
  const validateFile = (f) => {
    // reset errors
    setGlobalErrorList([]);
    //console.log("validate file", f, f.size, f.type);
    let errors = [];

    //  check file type
    if (!f.type.match('image/jpeg') && !f.type.match('image/png')) {
      errors.push(FILE_TYPE_ERROR);
    }

    // check file size
    if (f.size > 5 * 1024 * 1024) {
      errors.push(FILE_SIZE_LIMIT_ERROR);
    }

    if (errors.length > 0) {
      setGlobalErrorList(errors);
    }

    return errors.length === 0;
  }

  const handleDrop = (e) => {
    e.preventDefault();
    const dropFiles = Array.from(e.dataTransfer.files);
    //console.log("handle drop file", dropFiles);
    const file = dropFiles[0];

    // validate file type and size
    if (!validateFile(file)) {
      setIsDragOver(false);
      return;
    }

    let formData = new FormData();
    formData.append("image", file);
    setAvatarData(formData);
    setFileUrl(URL.createObjectURL(file));
    setIsDragOver(false);
    setGlobalInfo(UPLOAD_GUIDE_INFO);
  }

  const handleFileChange = (e) => {
    const chosenFiles = Array.from(e.target.files);
    //console.log("handle select file", chosenFiles);
    const file = chosenFiles[0];

    // validate file type and size
    if (!validateFile(file)) {
      return;
    }

    let formData = new FormData();
    formData.append("image", file);
    setAvatarData(formData);
    setFileUrl(URL.createObjectURL(file));
    setGlobalInfo(UPLOAD_GUIDE_INFO);
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    setIsDragOver(false);
  }

  useEffect(() => {
    // getProfileImage(token)
    //   .then((res) => {
    //     console.log("Edit User Profile Pic", res);
    //     if (res?.data?.length > 0) {
    //       setFileUrl(URL.createObjectURL(res?.data));
    //     }
    //   });
    if (loginState?.avatar) {
      setFileUrl(loginState.avatar);
    }
  }, []);

  const handleSubmit = (avatarData, fileUrl) => {
    //console.log("before submit", avatarData);
    saveProfileImage(token, avatarData)
      .then((res) => {
        //console.log("save User Profile Pic", res);
        if (res.data.status === 1) {
          //console.log("save User Profile Pic2", res);
          // upload success and update the Top nav user profile avatar
          dispatch(setAvatar(fileUrl));
          updateAvatarChannel.postMessage(fileUrl);
          setUploadSuccess(UPLOAD_SUCCESS);
          setAvatarData(null);
          setGlobalInfo(null);
        } else {
          setGlobalErrorList(res.data?.globalErrorMessageList);
        }
      }).catch((error) => {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
      });
  }

  const handleRestore = () => {
    setShowWarningPopup(true);
  }

  const handleAvatarRestore = () => {
    //console.log("handleAvatarRestore");
    setShowWarningPopup(false);
  }

  return (
    <Row>
      <Col xl={11} lg={11} md={11} sm={12}>
        <EditUserProfilePictureWarningPopup
          show={showWarningPopup}
          handleAvatarRestore={handleAvatarRestore}
          handleClose={() => setShowWarningPopup(false)}
        />
        <Card id="edit-user-profile-picture" className="p-4">
          <Card.Body className="text-start">
            <h1 id="mainContent" className="header1 text-start mb-3 blue bold h1-form-title">
              Profile Picture (Optional)
            </h1>
            <p>Use the tools below to update your profile picture.</p>
            {fileUrl ?
              <div>
                <Image
                  src={fileUrl}
                  alt="Uploaded Preview Avatar"
                  className='user-avatar-uploaded'
                ></Image>
              </div> :
              <div className="user-avatar-default text-center d-flex align-items-center justify-content-center">
                <span>{getInitials(loginState.firstName, loginState.lastName)}</span>
              </div>}
            <Row>
              <Col xs={12} md={8}>
                {/* Drag and drop area for uploading profile picture */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  //className='drag-drop-zone mt-3 text-center'
                  className={`mt-3 text-center ${isDragOver ? 'drag-drop-zone-drag-over' : 'drag-drop-zone'}`}
                >
                  <div><CloudUploadOutlined className='upload-icon' /></div>
                  <div className='mt-1'>
                    Drag your image or{" "}
                    <Form.Group className="mb-3 d-inline" controlId="avatar-pic">
                      <Form.Label className='browse-input-label'>browse</Form.Label>
                      <Form.Control
                        type="file"
                        name="avatar-pic"
                        accept={FILE_FORMAT}
                        //ref={fileInputRef}
                        onChange={handleFileChange}
                        //isInvalid={validationMsg.files?.inValid}
                        className="mb-3 d-none"
                      //title=""
                      //disabled={files?.length === 1}
                      />
                    </Form.Group>
                  </div>
                  <div className='picture-size-desc'>Maximum file size 5 MB.</div>
                </div>
              </Col>
            </Row>
            <div className='picture-type-desc mt-3'>Only support .jpg and .png images.</div>
            {globalErrorList.map((item, index) =>
              <>
                <PageInfo key={"errors_" + index} type="error" message={item} classes="mb-3"></PageInfo>
              </>
            )}
            {globalInfo && (

              <PageInfo type="info" message={globalInfo} classes="mb-3" />
            )}
            {uploadSuccess && (
              <PageInfo type="success" message={uploadSuccess} classes="mb-3" />
            )}
            <TextButton
              className="me-3 mt-3"
              variant="normal"
              onClick={() => handleSubmit(avatarData, fileUrl)}
              disabled={avatarData ? false : true}
              content={"Upload"}
            />
            <TextButton
              className="me-3 mt-3"
              variant="outline"
              onClick={handleRestore}
              content={"Restore Default"}
            />
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default EditUserProfilePicture;